import { MenuRoute } from '@blb-ventures/react-components/dist';
import styled from '@emotion/styled';
import CheckIcon from '@mui/icons-material/Check';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { Box, Container, useMediaQuery } from '@mui/material';
import { ContainerLoader } from '@web/common/components/loaders/container-loader';
import { Breadcrumb, BreadcrumbData } from '@web/common/components/microdata/breadcrumb-data';
import { ProductAggregatorData } from '@web/common/components/microdata/product-aggregator-data';
import { SEO } from '@web/common/components/seo/seo';
import { SortDrawer } from '@web/common/components/sort-drawer';
import { VEROO_ORIGIN } from '@web/common/constants/url';
import { externalEvents } from '@web/common/events/external-event';
import { useSavePromocode } from '@web/common/hooks/use-save-promocode';
import { getUTMParamsFromQuery } from '@web/common/hooks/use-utm-lead-event-create';
import { getDefaultPromocode } from '@web/common/promocode/get-default-promocode';
import { captureException } from '@web/common/sentry';
import { theme } from '@web/common/theme';
import { withApolloSSR } from '@web/common/with-apollo-ssr';
import { IndexLayout } from '@web/home/layout';
import { EmptyProductList } from '@web/store/components/empty-product-list';
import { MobileProductKindSelector } from '@web/store/components/mobile-product-kind-selector';
import { ProductKindFilter } from '@web/store/components/product/product-kinds-filter';
import { StoreCoffeeSearchToolbar } from '@web/store/components/store-coffee-search-toolbar';
import { StoreProductsGrid } from '@web/store/components/store-products-grid';
import { StoreSearchCoffeeFilter } from '@web/store/components/store-search-coffee-filter';
import { getProductKindOptionsDrawer } from '@web/store/get-product-kind-options-drawer';
import { formatProductToItem } from '@web/store/map-product-to-items';
import { normalizeString } from '@web/store/utils/normalize-string';
import {
  coffeeKindLabelDict,
  getSortOptionsParams,
  OrderByOptionsKeys,
  productKindDetails,
  productKindToQueryDict,
  SelectableProductKinds,
} from '@web/store/store-product-options';
import {
  GridOptionFieldsFragment,
  GridOptionsDocument,
  GridOptionsQuery,
  GridOptionsQueryVariables,
  useGridOptionsQuery,
} from 'core/lib/graphql/store/_gen/grid-options';
import { ProductFieldsFragment } from 'core/lib/graphql/store/_gen/product';
import {
  ProductsDocument,
  ProductsQuery,
  ProductsQueryVariables,
  useProductsQuery,
} from 'core/lib/graphql/store/_gen/products';
import {
  FinalityKind,
  LeadEventKind,
  ProductCoffeeKind,
  ProductKind,
} from 'core/lib/graphql/types.d';
import { useLeadEventCreateMutation } from 'core/lib/graphql/_gen/lead-event-create';
import { useMeQuery } from 'core/lib/graphql/_gen/me';
import { GetServerSideProps } from 'next';
import { useRouter } from 'next/router';
import { FC, MouseEvent, useCallback, useMemo, useState } from 'react';

const StyledContainer = styled(Container)`
  margin-top: ${props => props.theme.spacing(2)};
  padding-bottom: ${props => props.theme.spacing(10)};
`;

const StyledSortDrawer = styled(SortDrawer)`
  ${props => props.theme.breakpoints.up('md')} {
    display: none;
  }
`;

const StickyFilterWrapper = styled(Box)`
  position: sticky;
  top: 0;
  background-color: ${props => props.theme.palette.background.default};
  z-index: 100;
  padding-top: ${props => props.theme.spacing(0)};
  padding-bottom: ${props => props.theme.spacing(0)};
  margin-left: -${props => props.theme.spacing(2)};
  margin-right: -${props => props.theme.spacing(2)};
  padding-left: ${props => props.theme.spacing(2)};
  padding-right: ${props => props.theme.spacing(2)};

  ${props => props.theme.breakpoints.down('sm')} {
    padding-top: ${props => props.theme.spacing(0.5)};
  }
`;

type Drawers = 'productKinds' | 'roast';

const FINALITY = FinalityKind.Ecommerce;

export const StoreSearch: FC = () => {
  const smDown = useMediaQuery(theme.breakpoints.down('sm'));

  // queries
  const router = useRouter();
  const { kind, kinds, coffeeKind, coffeeKinds } = getSortOptionsParams(router.query);

  const basePromocode = getDefaultPromocode({
    code: typeof router.query.promocode === 'string' ? router.query.promocode : null,
    usage: FinalityKind.Ecommerce,
  });

  // View category mutation
  const [leadCreate] = useLeadEventCreateMutation();
  const [selectedKind, setSelectedKind] = useState<SelectableProductKinds | undefined>(kind);
  const viewCategory = useCallback(
    (data?: ProductsQuery) => {
      if (data?.products?.edges != null) {
        const prods = data.products.edges.map(it => it!.node!) as ProductFieldsFragment[];
        externalEvents.viewProductCategory(
          selectedKind ?? 'All',
          '',
          prods,
          basePromocode.code ?? undefined,
        );
        const UTMParams = getUTMParamsFromQuery(router.query);
        leadCreate({
          variables: {
            finality: FinalityKind.Ecommerce,
            kind: LeadEventKind.ProductView,
            items: prods.map(it => ({ product: it!.id, quantity: 1, price: it!.price })),
            promocode: basePromocode.code,
            ...UTMParams,
          },
        });
      }
    },
    [selectedKind, basePromocode.code, router.query, leadCreate],
  );

  // graphql
  const meQ = useMeQuery();
  const productsQ = useProductsQuery({
    variables: { kinds, coffeeKinds, finality: FINALITY, first: 100 },
    onCompleted: data => viewCategory(data),
  });
  const gridOptionsQ = useGridOptionsQuery({ variables: { name_Iexact: 'Torra' } });

  // states
  const [openDrawer, setOpenDrawer] = useState<Drawers | null>(null);
  const [selectedRoast, setSelectedRoast] = useState<string | undefined>();
  const [selectedOrderByOption, setSelectedOrderByOption] =
    useState<OrderByOptionsKeys>('-createdAt');
  const [selectedCoffeeKind, setSelectedCoffeeKind] = useState<ProductCoffeeKind | undefined>(
    coffeeKind,
  );
  const [searchText, setSearchText] = useState('');

  // Memos
  const allProducts = useMemo(
    () =>
      (productsQ.data?.products?.edges || []).map(it =>
        formatProductToItem(it!.node!, meQ.data?.me?.isSubscriber),
      ),
    [productsQ.data, meQ.data?.me],
  );

  // Filter products locally based on search text
  const products = useMemo(() => {
    if (!searchText.trim()) {
      return allProducts;
    }

    const normalizedSearch = normalizeString(searchText);
    return allProducts.filter(product => {
      const titleStr = typeof product.title === 'string' ? product.title : '';
      const subtitleStr = typeof product.subtitle === 'string' ? product.subtitle : '';

      const normalizedTitle = normalizeString(titleStr);
      const normalizedSubtitle = normalizeString(subtitleStr);

      return (
        normalizedTitle.includes(normalizedSearch) || normalizedSubtitle.includes(normalizedSearch)
      );
    });
  }, [allProducts, searchText]);

  // Handlers
  const handleProductKindChange = useCallback((_: MouseEvent, kindOpt?: SelectableProductKinds) => {
    setSelectedKind(kindOpt);
    setOpenDrawer(null);
  }, []);

  const handleCoffeeKindSelect = (coffeeKindOpt?: ProductCoffeeKind) => {
    const isSelected = selectedCoffeeKind === coffeeKindOpt;
    const currentValue = isSelected ? undefined : coffeeKindOpt;
    setSelectedCoffeeKind(currentValue);
  };

  const handleOrderByOptionChange = async (option: OrderByOptionsKeys) => {
    setSelectedOrderByOption(option);
    await productsQ.refetch({
      coffeeKinds: selectedCoffeeKind,
      kinds: selectedKind ? [selectedKind] : undefined,
      orderby: [option],
      gridOptionValue: selectedRoast,
    });
    setOpenDrawer(null);
  };

  const handleRoastSelect = useCallback(
    (roast?: string) => {
      setSelectedRoast(roast);
      productsQ.refetch({
        coffeeKinds: selectedCoffeeKind,
        kinds: selectedKind ? [selectedKind] : undefined,
        orderby: [selectedOrderByOption],
        gridOptionValue: roast,
      });
      setOpenDrawer(null);
    },
    [productsQ, setSelectedRoast, selectedKind, selectedCoffeeKind, selectedOrderByOption],
  );

  const handleSearchTextChange = (searchTxt: string) => {
    setSearchText(searchTxt);
  };

  const roastOptions = useMemo(
    () =>
      (gridOptionsQ.data?.gridOptions?.edges ?? []).map(
        it => it?.node,
      ) as GridOptionFieldsFragment[],
    [gridOptionsQ.data],
  );

  const roastMenu: MenuRoute[] = useMemo(() => {
    const currentRoastOptions = roastOptions.map(it =>
      it?.values.map(value => {
        const active = selectedRoast === value.id;
        return {
          label: value.name,
          onClick: () => {
            active ? handleRoastSelect('') : handleRoastSelect(value.id);
          },
          active,
          endIcon: { icon: active && <CheckIcon color="primary" fontSize="small" /> },
        } as MenuRoute;
      }),
    );
    return currentRoastOptions[0] || [];
  }, [selectedRoast, handleRoastSelect, roastOptions]);

  useSavePromocode();

  const defaultKind = kind ?? ProductKind.Coffee;

  const title = `${productKindDetails[defaultKind].label}${
    defaultKind === ProductKind.Coffee && selectedCoffeeKind != null
      ? ` - ${coffeeKindLabelDict[selectedCoffeeKind]}`
      : ''
  }`;

  const kindOptions = useMemo(
    () => getProductKindOptionsDrawer(router.query, selectedKind, handleProductKindChange),
    [handleProductKindChange, router.query, selectedKind],
  );

  const categoryBreadcrumb: Breadcrumb[] = useMemo(
    () => [
      {
        name: 'Veroo',
        url: `${VEROO_ORIGIN}`,
      },
      {
        name: 'Loja',
        url: `${VEROO_ORIGIN}/loja`,
      },
      {
        name: `${productKindDetails[kind ?? ProductKind.Coffee].label}`,
        url: `${VEROO_ORIGIN}/loja/categoria/${productKindToQueryDict[kind ?? ProductKind.Coffee]}`,
      },
    ],
    [kind],
  );
  const productsAggregatorData = useMemo(
    () =>
      productsQ.data?.products?.edges.map(prod => ({
        images: (prod?.node?.images ?? []).length > 0 ? prod?.node?.images[0].file?.url ?? '' : '',
        price: prod?.node?.price ?? 0,
      })) ?? [],
    [productsQ.data?.products?.edges],
  );

  return (
    <IndexLayout
      bottomNavChildren={
        <MobileProductKindSelector
          openDrawer={() => setOpenDrawer('productKinds')}
          currentKind={selectedKind}
        />
      }
    >
      <SEO title={`${title} - Loja`} />
      <StyledContainer fixed>
        <StyledSortDrawer
          title="Selecione a categoria"
          anchor="bottom"
          menuItems={kindOptions}
          open={openDrawer === 'productKinds'}
          onClose={() => setOpenDrawer(null)}
          onCloseIcon={<KeyboardArrowDownIcon fontSize="large" />}
        />
        <StyledSortDrawer
          title="Selecione a torra desejada"
          anchor="top"
          menuItems={roastMenu}
          open={openDrawer === 'roast'}
          onClose={() => setOpenDrawer(null)}
          onCloseIcon={<KeyboardArrowDownIcon fontSize="large" />}
        />
        <ProductKindFilter onKindChange={handleProductKindChange} selectedKind={selectedKind} />
        {selectedKind !== undefined && (
          <>
            <StoreCoffeeSearchToolbar
              smDown={smDown}
              selectedKind={selectedKind}
              selectedOrderByOption={selectedOrderByOption}
              searchText={searchText}
              onSearchTextChange={handleSearchTextChange}
              onOrderByChange={handleOrderByOptionChange}
            />
            {selectedKind === ProductKind.Coffee && (
              <StickyFilterWrapper>
                <StoreSearchCoffeeFilter
                  selectedCoffeeKind={selectedCoffeeKind}
                  onCoffeeKindChange={handleCoffeeKindSelect}
                  onRoastChange={handleRoastSelect}
                  selectedRoast={selectedRoast || ''}
                  roastMenu={roastMenu}
                  onOpenRoastDrawer={() => setOpenDrawer('roast')}
                  roastOptions={roastOptions}
                />
              </StickyFilterWrapper>
            )}
          </>
        )}
        <ContainerLoader loading={productsQ.loading}>
          {(productsQ.data?.products?.totalCount || 0) > 0 ? (
            <StoreProductsGrid items={products} />
          ) : (
            <Box py={4}>
              <EmptyProductList />
            </Box>
          )}
        </ContainerLoader>
      </StyledContainer>
      <BreadcrumbData breadcrumbList={categoryBreadcrumb} />
      <ProductAggregatorData
        products={productsAggregatorData}
        category={`${productKindDetails[kind ?? ProductKind.Coffee].label}`}
      />
    </IndexLayout>
  );
};

export const getServerSideProps: GetServerSideProps = withApolloSSR(
  async ({ apolloClient }, ctx) => {
    const { kinds, coffeeKinds } = getSortOptionsParams(ctx.query);
    try {
      await Promise.all([
        apolloClient.query<ProductsQuery, ProductsQueryVariables>({
          query: ProductsDocument,
          variables: { kinds, coffeeKinds, finality: FINALITY, first: 100 },
        }),
        apolloClient.query<GridOptionsQuery, GridOptionsQueryVariables>({
          query: GridOptionsDocument,
        }),
      ]);
    } catch (e) {
      captureException(e);
    }
    return { props: {} };
  },
);

export default StoreSearch;
